/// <amd-module name="VSS/Platform/Layout" />
import CreateReactClass = require("create-react-class");
import PropTypes = require("prop-types");
import React = require("react");
import ReactDOM = require("react-dom");
import { EventManagement } from "VSS/Core/EventManagement";
import {
    IObjectProperty,
    IObservable,
    IObservableObject,
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    ObservableArray,
    ObservableObject,
    ObservableValue
} from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { ICancelablePromise, makeCancelable, usePromise } from "VSS/Core/Util/Promise";
import { IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "./Context";
import { ContributionServiceEvent, IDataProviderQueryCompletedEvent, IVssContributionEventService, IVssContributionService } from "./Contribution";
import { IVssPerformanceService, PerformanceConstants } from "./Performance";
import { getCookie } from "./Util/Cookie";

// Add an import to the Theming service so that the theme is applied before first layout/render.
import Theming = require("./Theming");

// Monkey-patch react to have PropTypes and createClass, which were removed in
// React 16. This allows us to work in compat mode. This should be removed eventually
// but it's not critical.
(React as any).PropTypes = PropTypes;
(React as any).createClass = CreateReactClass;

/**
 * Basic function for building a css classlist string from and array of classes, where
 * one of more of the arguments may be null or undefined.
 *
 * @param classes Array of strings the represents the css class list.
 *
 * @example css("base", "active", x === 42 && "optional") will return "base active optional" if x === 42 or "base active" otherwise
 */
export function css(...classes: (string | undefined | null | false)[]): string {
    return classes
        .filter(c => c)
        .join(" ")
        .trim();
}

/**
 * Any IObservableProperty is used to represent a property thats value is an IObservable<T>.
 * When supplied to the VssComponent it represents a value from the props that should
 * be managed via the component.
 */
export interface IObservableProperty<TProps> {
    /**
     * The name of the property that should be observed.
     */
    propertyName: keyof TProps;

    /**
     * The delegate function that will be called when the properties underlying observable
     * sends a notification.
     */
    observer: (value: any, action?: string) => void;

    /**
     * Optional value that defines a single action that should be observed. If omitted all
     * actions will be subscribed too.
     */
    action?: string;
}

/**
 * This is an empty interface used to represent an object that contains a components
 * creation properties. It doesn't actaully define any values.
 */
export interface IVssComponentProperties {
    /**
     * Components may specify a css classe list that should be applied to the primary
     * element of the component when it is rendered.
     */
    className?: string;

    /**
     * Components MAY specify an order value which is a number > 0 which defines its
     * rendering order when multiple components target the same componentRegion. If the
     * order is NOT specified it defaults to Number.MAX_VALUE.
     */
    componentOrder?: number;

    /**
     * Key value for this component that MUST be set when the component is rendered
     * into a set of components.
     */
    key?: string | number;

    /**
     * Any of the properties MAY be accessed as an IObservable.
     */
    [property: string]: IObservable<any> | any;
}

/**
 * An IVssComponentDetail describes the information needed to create an instance of a component
 */
export interface IVssComponentDetail {
    /**
     * componentId is used to uniquely identify this component instance. The id is
     * used to determine new vs existing component mappings when the mappings are
     * updated.
     */
    componentId: string;

    /**
     * componentType is the name the component is registered with.
     */
    componentType: string;

    /**
     * componentProperties are the set of properties used to create the component.
     */
    componentProperties?: IVssComponentProperties;

    /**
     * If the componentType is "componentProvider" then the providerName should be used
     * to resolve a service that returns an IComponentProviderService. This is used to
     * resolves the component(s) encapsulated in this component.
     */
    providerName?: string;
}

/**
 * An IComponentProvider is used to retrieve a set of component details. This is frequently
 * used when a component is used as a placeholder for a dynamic set of components.
 */
export interface IComponentProvider<T> {
    /**
     * getComponents is called with an object that tells the provider how to determine
     * the component set that should be returned.
     */
    getComponents: (props: T) => JSX.Element[];
}

/**
 * IComponentProviderService is a component provider implemented through a service. This
 * interface should be used when the caller is going to retrieve instances of the provider
 * through getService on the pageContext.
 */
export interface IComponentProviderService<T> extends IComponentProvider<T>, IVssService {}

/**
 * Global component registry for UI components used by the layout manager
 */
export const Components: IObservableObject<React.ComponentClass<IVssComponentProperties> | ((props?: any) => JSX.Element) | React.SFC<any>> =
    new ObservableObject<React.ComponentClass<IVssComponentProperties> | ((props?: any) => JSX.Element) | React.SFC<any>>();

/**
 * Basic component context available to all components that are rendered within a properly
 * configured React.DOM tree. This applies to trees rendered with page regions.
 */
export interface IVssComponentContext {
    pageContext: IVssPageContext;
}

/**
 * The IBoundObservable represents an IObservableProperty that has been bound to the property.
 */
interface IBoundObservable {
    /**
     * Instance of the observable property we are bound too.
     */
    observable: IObservable<any>;

    /**
     * The observing delegate used for this observable.
     */
    observer: (value: any, action?: string) => void;

    /**
     * The optional action this observer is bound too.
     */
    action?: string;
}

// We always use the context with an actual value, so setting an empty object as the default value here.
// (Note: {} is not actually a valid component context - pageContext is not optional - so casting here).
export const VssComponentContext = React.createContext<IVssComponentContext>({} as IVssComponentContext);

/**
 * Base class for any component which needs to be registered with the layout manager.
 * The VssLayoutManager is designed to help manage the rendering of components based
 * on either componentType or componentRegion. Any component can register with the
 * layout manager but using the VssComponent base class ensures some basic capabilities
 * for your component.
 */
export abstract class VssComponent<P extends IVssComponentProperties = IVssComponentProperties, S = {}, SS = {}> extends React.Component<P, S, SS> {
    public static uniqueNumber: number = 0;
    public static defaultProps = {};
    public static contextType = VssComponentContext;

    public static register(componentType: string, reactClass: React.ComponentClass<IVssComponentProperties> | ((props?: any) => JSX.Element)): void {
        Components.add(componentType, reactClass);
    }

    private observables?: { [propertyName: string]: IBoundObservable[] };
    private observers?: { [propertyName in keyof P]?: IObservableProperty<P>[] };
    private timers?: TimerManagement;
    private events?: EventManagement;
    private promises: ICancelablePromise<any>[];
    private unmountCallbacks: Array<{ (): void }> = [];

    protected componentElement: HTMLElement | null;
    protected componentId: number;

    public context!: React.ContextType<typeof VssComponentContext>;

    constructor(props: P, context?: IVssComponentContext) {
        super(props, context);

        // Save the context id one was supplied, this will allow the derived component
        // to use the context on the object during initialization.
        if (context) {
            this.context = context;
        }

        // Each component has a unique number for its componentId.
        this.componentId = ++VssComponent.uniqueNumber;

        // This is only for back compat, suppress react warning for this class
        (VssComponent.prototype.componentWillReceiveProps as any).__suppressDeprecationWarning = true;
    }

    /**
     * When components are being created the componentDidMount call is made when the
     * component has been mounted.
     *
     * Derived components that implement componentDidMount need to be sure to call
     * super.componentDidMount.
     */
    public componentDidMount(): void {}

    /**
     * When components are updated the componentDidUpdate call is made after the changes
     * have been made to the actual DOM. This is the opportunity for all components to
     * update any internal state.
     *
     * Derived components that implement componentDidUpdate need to be sure to call
     * super.componentDidUpdate.
     */
    public componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot?: SS): void {
        this._manageObservables(this.props);
    }

    /**
     * @deprecated This method is deprecated, use getDerivedStateFromProps instead.
     */
    public componentWillReceiveProps(newProps: P): void {}

    /**
     * When components are unmounted resources they own are cleaned up.
     *
     * Derived components that implement componentWillUnmount need to be sure to call
     * super.componentWillUnmount.
     */
    public componentWillUnmount(): void {
        // Release any previously managed observables.
        this._manageObservables();

        // If any event listeners were attached to event targets throughout the components
        // lifetime, we will remove them when the component is unmounting.
        if (this.events) {
            this.events.removeAllListeners();
            delete this.events;
        }

        if (this.timers) {
            this.timers.clearAllTimers();
            delete this.timers;
        }

        if (this.promises) {
            for (const promise of this.promises) {
                promise.cancel();
            }
        }

        for (const unmountCallback of this.unmountCallbacks) {
            unmountCallback.call(this);
        }
    }

    /**
     * addEventListener provides a component level method for adding event listeners
     * to any EventTarget in the DOM and tracking them with the scope of this component.
     * This ensures that when the component is unmounted, any outstanding listeners
     * will be removed.
     *
     * @param target - The EventTarget to add the listener too.
     *
     * @param type - The event type the listener is signing up too.
     *
     * @param listener - The listener callback delegate to be called when the events occur.
     *
     * @param useCapture - Use capture mode for event phase.
     */
    protected addEventListener(target: EventTarget, type: string, listener: (event?: Event) => boolean | void, useCapture?: boolean): void {
        if (!this.events) {
            this.events = new EventManagement();
        }

        this.events.addEventListener(target, type, listener, useCapture);
    }

    /**
     * Registers a callback method that gets run during componentWillUnmount
     *
     * @param callback The callback method to execute on unmount
     */
    protected addUnmountCallback(callback: () => void) {
        this.unmountCallbacks.push(callback);
    }

    /**
     * removeEventListener provides a way to remove an event listener from any EventTarget
     * in the DOM and will remove the components tracking reference on this listener.
     *
     * @param target - The EventTarget to remove the listener from.
     *
     * @param type - The event type the listener was signed up too.
     *
     * @param listener - The listener callback delegate to remove.
     *
     * @param useCapture - Was the event using capture mode.
     */
    protected removeEventListener(target: EventTarget, type: string, listener: (event?: Event) => boolean | void, useCapture?: boolean): void {
        if (this.events) {
            this.events.removeEventListener(target, type, listener, useCapture);
        }
    }

    /**
     * clearInterval is used to stop the series of callbacks that was setup through setInterval.
     *
     * @param intervalId - The id returned from eh setInterval call that you want stopped.
     */
    protected clearInterval(intervalId: number): void {
        if (this.timers) {
            this.timers.clearInterval(intervalId);
        }
    }

    /**
     * clearTimeout is used to stop a timeout callback that was setup through setTimeout.
     *
     * @param timeoutId - The id returned from eh setTimeout call that you want stopped.
     */
    protected clearTimeout(timeoutId: number): void {
        if (this.timers) {
            this.timers.clearTimeout(timeoutId);
        }
    }

    /**
     * setInterval is used to setup a callback that is called on an interval.
     *
     * @param callback - The callback that should be called each interval time period.
     *
     * @param milliseconds - The number of milliseconds between each callback.
     *
     * @param args - Optional variable argument list passed to the callback.
     *
     * @returns - returns a handle to the interval, this can be used to cancel through clearInterval method.
     */
    protected setInterval(callback: (...args: any[]) => void, milliseconds: number, ...args: any[]): number {
        if (!this.timers) {
            this.timers = new TimerManagement();
        }

        return this.timers.setInterval(callback, milliseconds, ...args);
    }

    /**
     * setTimeout is used to setup a onetime callback that is called after the specified timeout.
     *
     * @param callback - The callback that should be called when the time period has elapsed.
     *
     * @param milliseconds - The number of milliseconds before the callback should be called.
     *  Even if a timeout of 0 is used the callback will be executed asynchronouly.
     *
     * @param args - Optional variable argument list passed to the callback.
     *
     * @returns - returns a handle to the timeout, this can be used to cancel through clearTimeout method.
     */
    protected setTimeout(callback: (...args: any[]) => void, milliseconds?: number, ...args: any[]): number {
        if (!this.timers) {
            this.timers = new TimerManagement();
        }

        return this.timers.setTimeout(callback, milliseconds, ...args);
    }

    /**
     * Tracks a promise so that it will be cancelled on component unmount. This prevents
     * the promise's callbacks from being invoked after the component has unmounted
     *
     * @param promise Promise to track
     */
    protected trackPromise<T = any>(promise: Promise<T>): ICancelablePromise<T> {
        if (!this.promises) {
            this.promises = [];
        }

        const cancelablePromise = makeCancelable<T>(promise);

        // Remove the tracked promise when it completes
        promise.then(
            () => {
                this.promises = this.promises.filter(tp => tp !== cancelablePromise);
            },
            () => {
                this.promises = this.promises.filter(tp => tp !== cancelablePromise);
            }
        );

        this.promises.push(cancelablePromise);
        return cancelablePromise;
    }

    /**
     * observe describes a value from the component properties that is an IObservable that needs
     * to be subscribed to. This will manage the subscriptions for these properties over the
     * lifecycle of the component.
     *
     * @param property - Name of the property that is an Observable
     *
     * @param observer - The delegate that is called when the Observable sends a matching notification.
     *
     * @param action - An optional action, which defines a single action from the observer that should
     *  be subscribed to. If omitted the subscription will be made to all actions.
     */
    protected observe(properties: IObservableProperty<P>[]): void {
        // Create a new set of observers, this call must have all of them.
        this.observers = {};

        // Only create the observables the first time we setup for observers. We can't wipe out this
        // since it holds our current observable state.
        if (!this.observables) {
            this.observables = {};
        }

        // Add the observed properties to the set of observed propeties.
        for (const property of properties) {
            if (!this.observers[property.propertyName]) {
                this.observers[property.propertyName] = [];
            }

            this.observers[property.propertyName]!.push(property);
        }

        // Update the state of our observables.
        this._manageObservables(this.props);
    }

    private _manageObservables(newProps?: IVssComponentProperties): void {
        if (this.observables) {
            // Clean up any existing observables, we will reattach the ones we need.
            for (const propertyName in this.observables) {
                for (const boundObservable of this.observables[propertyName]) {
                    boundObservable.observable.unsubscribe(boundObservable.observer, boundObservable.action);
                }

                delete this.observables[propertyName];
            }

            // If any observable properties were supplied we will subscribe to them.
            if (newProps && this.observers) {
                for (const propertyName in this.observers) {
                    const observable = newProps[propertyName] as IObservable<any>;

                    // Ensure this is a valid IObservable before we subscribe too it.
                    if (observable && observable.subscribe && (observable as any).unsubscribe) {
                        for (const observer of this.observers[propertyName]!) {
                            observable.subscribe(observer.observer, observer.action);

                            if (!this.observables[propertyName]) {
                                this.observables[propertyName] = [];
                            }

                            this.observables[propertyName].push({ observable: observable, observer: observer.observer, action: observer.action });
                        }
                    }
                }
            }
        }
    }
}

/**
 * The IVssLayoutManager is used to render the components on a page. It is used to
 * render a fresh page as well as track and update the page components as the page
 * changes.
 */
export interface IVssLayoutManager extends IVssService {
    /**
     * getComponentsForRegion is used to retrieve the set of component details
     * mapped to a given region.
     *
     * @param componentRegion - This is the logical name of the region being requested.
     *  If the componentProperties have an "order" property they will be returned
     *  in sorted order. If not, there is no defined order.
     *
     * @return - Returns the set of component details registered for the supplied region.
     */
    getComponentsForRegion: (componentRegion: string) => IReadonlyObservableArray<IVssComponentDetail>;

    /**
     * renderCallout is used to render a top level component that is owned by the page.
     * Callers should use this function instead of creating their own top-level elements
     * as containers.
     *
     * @param callout - This is a function that is called the retrieve the elements that
     *  should be rendered within the root element. The callout MUST call onDismiss when
     *  the callout should be closed.
     */
    renderCallout: (callout: (onDismiss: () => void) => JSX.Element) => void;

    /**
     * Gets whether the page is currently in full screen mode
     */
    getFullScreenMode(): IReadonlyObservableValue<boolean>;

    /**
     * Enter or exit full screen mode
     *
     * @param fullScreenMode True to enter full-screen mode, false to exit.
     */
    setFullScreenMode(fullScreenMode: boolean): void;

    /**
     * Toggle the current full-screen mode
     */
    toggleFullScreenMode(): void;
}

/**
 * This is the basic LayoutManager used by the core platform.
 */
class VssLayoutManager extends VssService implements IVssLayoutManager {
    private callouts: Map<number, () => void> | undefined;
    private contributionService: IVssContributionService;
    private regionMapping: { [componentRegion: string]: ObservableArray<IVssComponentDetail> } = {};
    private _fullScreenMode: ObservableValue<boolean>;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        // We will sign-up for updates to contribution data and merge in any updated region mappings.
        this.contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.contributionService.subscribe(this.onDataProviderQueryEnd, ContributionServiceEvent.DataProviderQueryCompleted);

        // Get the initial set of component region mappings.
        const details = this.contributionService.getData<{ [componentRegion: string]: IVssComponentDetail[] }>("ms.vss-web.component-data") || {};
        Object.keys(details).forEach(key => {
            this.regionMapping[key] = new ObservableArray<IVssComponentDetail>(details[key].sort(this.compareDetails));
        });
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        // Remove our subscription to the contribution service when we unload.
        this.contributionService.unsubscribe(this.onDataProviderQueryEnd, ContributionServiceEvent.DataProviderQueryCompleted);
    }

    public getComponentsForRegion(componentRegion: string): ObservableArray<IVssComponentDetail> {
        if (!this.regionMapping[componentRegion]) {
            this.regionMapping[componentRegion] = new ObservableArray<IVssComponentDetail>();
        }
        return this.regionMapping[componentRegion];
    }

    public renderCallout(callout: (onDismiss: () => void) => JSX.Element): void {
        // Create a top level element for the panel to be hosted inside.
        const calloutElement = document.createElement("div");
        document.body.appendChild(calloutElement);
        const id = calloutId++;

        // Have a local function for closing the callout.
        const onDismiss = (): void => {
            if (this.callouts && this.callouts.has(id)) {
                ReactDOM.unmountComponentAtNode(calloutElement);
                document.body.removeChild(calloutElement);
                this.callouts.delete(id);
            }
        };

        if (this.callouts === undefined) {
            this.callouts = new Map<number, () => void>();

            document.body.addEventListener("fpsLoading", this.onFpsLoading);
        }

        this.callouts.set(id, onDismiss);

        // Render the root component with the results of the callout as the children.
        ReactDOM.render(React.createElement(ReactRootComponent, { pageContext: this.pageContext }, callout(onDismiss)), calloutElement);
    }

    public getFullScreenMode(): IReadonlyObservableValue<boolean> {
        if (!this._fullScreenMode) {
            this._fullScreenMode = new ObservableValue<boolean>(window.location.href.indexOf("fullScreen=true") !== -1);
        }

        return this._fullScreenMode;
    }

    public setFullScreenMode(fullScreenMode: boolean): void {
        if (!this._fullScreenMode) {
            this._fullScreenMode = new ObservableValue<boolean>(fullScreenMode);
        } else {
            this._fullScreenMode.value = fullScreenMode;
        }

        if (fullScreenMode) {
            document.body && document.body.classList.add("full-screen-mode");
        } else {
            document.body && document.body.classList.remove("full-screen-mode");
        }
    }

    public toggleFullScreenMode(): void {
        this.setFullScreenMode(!this.getFullScreenMode().value);
    }

    private compareDetails = (detail1: IVssComponentDetail, detail2: IVssComponentDetail): number => {
        return (
            ((detail1.componentProperties && detail1.componentProperties.componentOrder) || Number.MAX_VALUE) -
            ((detail2.componentProperties && detail2.componentProperties.componentOrder) || Number.MAX_VALUE)
        );
    };

    private onDataProviderQueryEnd = (endQueryEvent: IDataProviderQueryCompletedEvent): void => {
        // If any component were included in the contribution query we will
        // update the set region mappings.
        if (endQueryEvent.dataProviders) {
            const componentData = endQueryEvent.dataProviders["ms.vss-web.component-data"] as
                | { [componentRegion: string]: IVssComponentDetail[] }
                | undefined;
            if (componentData) {
                for (const componentRegion in componentData) {
                    const newComponents = componentData[componentRegion];
                    const existingComponents = this.regionMapping[componentRegion];

                    // If this region is new we just use the current details, otherwise
                    // we will append them to the current list and then sort.
                    if (!existingComponents) {
                        this.regionMapping[componentRegion] = new ObservableArray<IVssComponentDetail>();
                    } else {
                        // Merge the components into the existing mapping based on Id.
                        for (const component of existingComponents.value) {
                            if (!newComponents.some(newComponent => component.componentId === newComponent.componentId)) {
                                newComponents.push(component);
                            }
                        }
                    }
                    this.regionMapping[componentRegion].splice(
                        0,
                        this.regionMapping[componentRegion].length,
                        ...newComponents.sort(this.compareDetails)
                    );
                }
            }
        }
    };

    private onFpsLoading = (ev: CustomEvent) => {
        if (this.callouts) {
            this.callouts.forEach((cleanup, id) => {
                cleanup();
            });

            this.callouts = undefined;
        }

        document.body.removeEventListener("fpsLoading", this.onFpsLoading);
    };
}

Services.add("IVssLayoutManager", { serviceFactory: VssLayoutManager });

let calloutId = 0;

/**
 * Startup service which is used to orchestrate rendering the page.
 *
 * This is necessary as a separate service from the layout manager since components
 * called during renderPage cannot otherwise obtain the layout service if renderPage
 * is called during the layout service's serviceStart.
 */
class VssPageRenderingService extends VssService {
    private pageRegions: NodeListOf<HTMLElement>;
    private reactRootKey: number = 0;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        // Select all the elements that are marked as component regions
        this.pageRegions = document.querySelectorAll("[data-componentregion]");

        // Render the page when the platform is initialized (covers initial load and FPS)
        this.pageContext.platformInitialized.subscribe(platformInitialized => {
            if (platformInitialized) {
                this.renderPage();
            }
        });
    }

    private renderPage(): void {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.addSplitTiming("VssPageRenderingService.renderPage.start");

        // Apply initial full-screen mode classes
        const fullScreenService = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        fullScreenService.setFullScreenMode(fullScreenService.getFullScreenMode().value);

        let componentCount = this.pageRegions.length;
        const handleComponentRendered = () => {
            if (--componentCount === 0) {
                if ($DEBUG) {
                    // Break on First-render when the cookie is set
                    if (getCookie(PerformanceConstants.BreakOnFirstRenderCookie) === "enabled") {
                        debugger;
                    }
                }

                const contributedEventService = this.pageContext.getService<IVssContributionEventService>("IVssContributionEventService");
                contributedEventService.fireEvent("page-rendered", {}, true);
            }
        };

        // Render each of the components using the value of the data-component as the componentRegion
        for (let elementIndex = 0; elementIndex < this.pageRegions.length; elementIndex++) {
            const componentRegion = this.pageRegions[elementIndex].getAttribute("data-componentregion");
            if (componentRegion) {
                ReactDOM.render(
                    React.createElement(
                        ReactRootComponent,
                        { key: this.reactRootKey++, pageContext: this.pageContext },
                        React.createElement(RegionComponent, { componentRegion: componentRegion, className: "full-size" }, null)
                    ),
                    this.pageRegions[elementIndex],
                    handleComponentRendered
                );
            } else {
                handleComponentRendered();
            }
        }

        performanceService.addSplitTiming("VssPageRenderingService.renderPage.end");
    }
}

// Start the page rendering service to kick off rendering the page
Services.add("VssPageRenderingService", { serviceFactory: VssPageRenderingService, options: ServiceOptions.Persistant | ServiceOptions.Startup });

/**
 * When creating a new React rendering tree using ReactDOM.render the caller should
 * use a ReactRootComponent and supply the required properties. This will ensure the
 * React component tree has the appropriate context setup.
 */
export interface IReactRootComponentProperties {
    pageContext: IVssPageContext;
}

/**
 * A ReactRootComponent is used to house the root of a React tree, this should be used anytime
 * ReactDOM.render is used to encapsulate the needed state management of the tree.
 *
 * This component MUST only have a single child, and it will not add any DOM to the produced
 * structure.
 */
export class ReactRootComponent extends VssComponent<IReactRootComponentProperties, {}> {
    public render(): React.ReactNode {
        const root = React.createElement(
            VssComponentContext.Provider,
            {
                value: {
                    pageContext: this.props.pageContext
                }
            },
            this.props.children
        );

        if ($DEBUG) {
            if (getCookie(PerformanceConstants.ReactStrictModeCookie) === "enabled") {
                return React.createElement(React.StrictMode, undefined, root);
            }
        }

        return root;
    }
}

/**
 * Properties used to render a named region
 */
export interface IRegionComponentProperties extends IVssComponentProperties {
    /**
     * Name of the region that is being rendered.
     */
    componentRegion: string;

    /**
     * Optional way to pass in properties for the child components.
     */
    childComponentProperties?: IVssComponentProperties;

    /**
     * Optional fallback component to show if no child components are available.
     */
    fallbackComponent?: () => JSX.Element;
}

/**
 * RegionComponent is used to render the component defined within a region
 */
export class RegionComponent extends VssComponent<IRegionComponentProperties, {}> {
    public render(): JSX.Element {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        const childComponents = layoutManager.getComponentsForRegion(this.props.componentRegion);
        
        return childComponents.length === 0 && this.props.fallbackComponent
            ? this.props.fallbackComponent()
            : React.createElement(
                  "div",
                  {
                      className: css(this.props.className, "region-" + this.props.componentRegion),
                      "data-renderedregion": this.props.componentRegion,
                      id: this.props.id,
                      role: this.props.role
                  },
                  childComponents.value.map((childComponent, childIndex) => {
                      return React.createElement(WrappedComponent, {
                          key: childIndex,
                          ...this.props.childComponentProperties,
                          ...childComponent.componentProperties,
                          wrappedType: childComponent.componentType
                      });
                  })
              );
    }
}

Components.add("ms.vss-web.region", RegionComponent);

/**
 * IWrappedComponentProperties is used to create a wrapper component that will render
 * the underlying component specified as soon as it is registered.
 */
export interface IWrappedComponentProperties<T extends IVssComponentProperties = IVssComponentProperties> extends IVssComponentProperties {
    /**
     * The type of component to render when once the component is registered.
     */
    wrappedType: string;

    /**
     * Optional dependencies that need to be resolved when rendering this component.
     */
    dependencies?: string[];

    /**
     * The ref to apply to the wrapped component. This allows the caller to retrieve
     * a reference to the wrapped component instead of the wrapper.
     */
    wrappedRef?: React.Ref<any>;

    /**
     * Optional way to pass in properties for the wrapped component. If this is not
     * specified, all of the WrappedComponent's own props will be pass as props to
     * the created component.
     */
    wrappedProps?: T | null;

    /**
     * Component to show while dependencies are being resolved
     */
    loadingComponent?: () => JSX.Element;

    /**
     * Callback method invoked after the wrapped component's dependencies are resolved.
     *
     * @returns May return a promise in which case this component will not render the
     *          wrappedType until the returned promise has been resolved.
     */
    onDependenciesResolved?: () => Promise<void> | void;

    /**
     * The wrapped properties is designed to support wrapping any react component. To this end
     * we will allow any property to be passed.
     */
    [propertyName: string]: any;
}

/**
 * WrappedComponent is used to create and manage an underlying component.
 * The underlying component is defined by the componentType given to the wrapper
 * through the properties and is resovled either synchoronously or asynchronously
 * via Component registrations.
 */
export function WrappedComponent<T extends IVssComponentProperties = IVssComponentProperties>(
    props: IWrappedComponentProperties<T> & { children?: React.ReactNode }
) {
    const context = useComponentContext();

    const { trackPromise } = usePromise();

    let dependencies: string[] | undefined;
    if (props.dependencies) {
        const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const unresolvedDependencies = props.dependencies.filter(d => !contributionService.isContributionResolved(d));
        if (unresolvedDependencies.length) {
            dependencies = unresolvedDependencies;
        }
    }

    const [componentClass, setComponentClass] = React.useState<
        React.ComponentClass<IVssComponentProperties, any> | ((props?: any) => JSX.Element) | React.SFC<any> | undefined
    >(() => Components.get(props.wrappedType));
    const [dependenciesResolved, setDependenciesResolved] = React.useState(!dependencies && !props.onDependenciesResolved);
    const [showLoadingComponent, setShowLoadingComponents] = React.useState(false);

    // Load dependencies if requested
    React.useEffect(() => {
        const handleDependenciesLoaded = async () => {
            if (props.onDependenciesResolved) {
                const resolutionPromise = props.onDependenciesResolved();
                if (resolutionPromise) {
                    try {
                        await trackPromise(resolutionPromise);
                    } catch {
                        // Promise might be canceled or failed, ignore
                    }
                }
            }
        };

        // If the caller defined a set of async dependencies, we will start to load them now.
        if (dependencies) {
            const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
            trackPromise(contributionService.getContributionsAsync(props.dependencies || []))
                .then(async () => {
                    await handleDependenciesLoaded();

                    setDependenciesResolved(true);
                })
                .catch(e => {
                    // If the promise was canceled ignore, otherwise log
                    if (e && !e.canceled && !e.isCanceled) {
                        if ($DEBUG) {
                            console.error(`Could not get dependencies for wrapped component with type ${props.wrappedType}`, e);
                        } else {
                            console.error(e);
                        }
                    }
                });
        } else if (props.onDependenciesResolved) {
            handleDependenciesLoaded().then(() => {
                setDependenciesResolved(true);
            });
        }
    }, [props.dependencies]);

    // Wait for component if not yet available
    React.useEffect(() => {
        // If the componentClass isn't available we will signup for new component class
        // registrations and when our wrapped component is available we will re-render.
        const componentClass = Components.get(props.wrappedType) as (() => JSX.Element) | undefined;
        if (!componentClass) {
            const handler = (componentEntry: IObjectProperty<() => JSX.Element>) => {
                if (componentEntry.key === props.wrappedType) {
                    Components.unsubscribe(handler, "add");
                    setComponentClass(() => componentEntry.value);
                }
            };

            Components.subscribe(handler, "add");

            return () => {
                Components.unsubscribe(handler, "add");
            };
        } else {
            setComponentClass(() => componentClass);
        }
    }, [props.wrappedType]);

    React.useEffect(() => {
        // Component is mounted
        if (props.loadingComponent && (!componentClass || !dependenciesResolved)) {
            // Start timer
            const timerId = setTimeout(() => {
                setShowLoadingComponents(true);
            }, 100);

            return () => {
                clearTimeout(timerId);
            };
        }
    }, [props.loadingComponent]);

    // Render
    return (
        (componentClass &&
            dependenciesResolved &&
            React.createElement(componentClass, { ref: props.wrappedRef, ...(props.wrappedProps || props) }, props.children)) ||
        (showLoadingComponent && props.loadingComponent && props.loadingComponent()) ||
        null
    );
}

/**
 * Custom hook to use the component context in functional components
 */
export function useComponentContext(): IVssComponentContext {
    return React.useContext(VssComponentContext);
}

/**
 * Custom hook to use the page context in functional components
 */
export function usePageContext(): IVssPageContext {
    return useComponentContext().pageContext;
}

export function useService<TService>(serviceId: string): TService {
    const pageContext = usePageContext();
    return React.useMemo(() => pageContext.getService<TService>(serviceId), [pageContext]);
}

/**
 * Custom hook to track if the component is mounted
 */
export function useIsMounted(): () => boolean {
    const isMounted = React.useRef(false);
    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    return React.useCallback(() => isMounted.current, []);
}
